/**
 * Hook: Push-Status für genau dieses Gerät
 *
 * Wird in den Profileinstellungen (Priority-Support-Sektion) genutzt, um
 * dem User zu zeigen, ob die Push-Subscription auf diesem Gerät aktiv,
 * inaktiv oder abgelaufen ist — und ihm passende Aktionen anzubieten:
 *
 *   - 'inactive'         → Button "Auf diesem Gerät aktivieren"
 *   - 'stale'            → Button "Neu verbinden" (Backend-Sub deaktiviert)
 *   - 'active'           → Status-Anzeige + optionaler Test-Push
 *   - 'permission-denied' / 'unsupported' / 'ios-needs-pwa' → Hinweistext
 *
 * Aktionen lösen automatisches Refresh aus, damit die Anzeige stimmt.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchDevicePushStatus,
  enablePushNotifications,
  forceResubscribePush,
  disablePushNotifications,
  sendTestPushThisDevice,
  type DevicePushStatus
} from '../services/support/pushSubscriptionService';

interface UseDevicePushStatusOptions {
  enabled?: boolean;
}

interface UseDevicePushStatusResult {
  status: DevicePushStatus | null;
  isLoading: boolean;
  isWorking: boolean;
  error: string | null;
  /** Successmeldung nach Aktion (z.B. "Test-Push gesendet"). */
  message: string | null;
  /** Auf diesem Gerät erstmals subscriben. */
  enable: () => Promise<void>;
  /** Bestehende Sub verwerfen + frisch subscriben. */
  reconnect: () => Promise<void>;
  /** Subscription auf diesem Gerät entfernen. */
  disable: () => Promise<void>;
  /** Test-Push gezielt an dieses Gerät schicken. */
  sendTestToThisDevice: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useDevicePushStatus(
  options: UseDevicePushStatusOptions = {}
): UseDevicePushStatusResult {
  const enabled = options.enabled !== false;
  const [status, setStatus] = useState<DevicePushStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    setIsLoading(true);
    try {
      const next = await fetchDevicePushStatus();
      setStatus(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status konnte nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setStatus(null);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  const wrap = useCallback(
    async (action: () => Promise<void>, successMessage?: string): Promise<void> => {
      setError(null);
      setMessage(null);
      setIsWorking(true);
      try {
        await action();
        if (successMessage) setMessage(successMessage);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Aktion fehlgeschlagen');
        throw err;
      } finally {
        setIsWorking(false);
        await refresh();
      }
    },
    [refresh]
  );

  const enable = useCallback(
    () => wrap(enablePushNotifications, 'Push auf diesem Gerät aktiviert.'),
    [wrap]
  );

  const reconnect = useCallback(
    () => wrap(forceResubscribePush, 'Verbindung erneuert.'),
    [wrap]
  );

  const disable = useCallback(
    () => wrap(disablePushNotifications, 'Push auf diesem Gerät deaktiviert.'),
    [wrap]
  );

  const sendTestToThisDevice = useCallback(
    () =>
      wrap(
        sendTestPushThisDevice,
        'Test-Push gesendet — sollte gleich auf diesem Gerät erscheinen.'
      ),
    [wrap]
  );

  return {
    status,
    isLoading,
    isWorking,
    error,
    message,
    enable,
    reconnect,
    disable,
    sendTestToThisDevice,
    refresh
  };
}
